import type { CSSProperties, HTMLAttributes } from "react";
import { MARQUE, type Marque } from "./marque";

/**
 * L'icône de la marque (rail, barre de titre), qui prend la COULEUR D'ACCENT
 * quand la marque le veut (`iconeALaCouleur`) — Kayla, 04-11-2026 : « si je
 * mets le thème en blanc, il soit en blanc […] avec un peu de métallisé, qu'on
 * garde l'effet de perspective ».
 *
 * Deux couches sur le même pochoir (le PNG de la marque) :
 *  1. le fond : le PNG sert de MASQUE, rempli d'un dégradé tiré de
 *     `--color-mark` (clair en haut, sombre en bas) + un reflet blanc en biais
 *     — c'est le métal ;
 *  2. le relief : le PNG lui-même en niveaux de gris, fondu en `soft-light`
 *     par-dessus — les plis de l'aile et de la bulle restent, quelle que soit
 *     la couleur. `isolation: isolate` borne le fondu à l'icône.
 *
 * Le dégradé lit `var(--color-mark)` : il suit le sélecteur de couleur en
 * direct, sans état React. Sans `iconeALaCouleur`, c'est le fichier tel quel.
 */
export function IconeDeMarque({
  marque = MARQUE,
  className,
  style,
  ...reste
}: { marque?: Marque } & HTMLAttributes<HTMLElement>) {
  const nom = marque.id === "dozka" ? "Dozka" : marque.nom;

  if (!marque.iconeALaCouleur) {
    return <img src={marque.icone} alt={nom} className={className} style={style} {...reste} />;
  }

  const pochoir = `url("${marque.icone}") center / contain no-repeat`;
  const metal: CSSProperties = {
    position: "absolute",
    inset: 0,
    WebkitMask: pochoir,
    mask: pochoir,
    background: [
      "linear-gradient(115deg, transparent 30%, rgb(255 255 255 / 0.55) 46%, transparent 58%)",
      "linear-gradient(160deg, color-mix(in srgb, var(--color-mark) 40%, white) 0%, var(--color-mark) 45%, color-mix(in srgb, var(--color-mark) 55%, black) 100%)",
    ].join(", "),
  };
  const relief: CSSProperties = {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "contain",
    filter: "grayscale(1) brightness(1.9) contrast(1.3)",
    mixBlendMode: "soft-light",
  };

  return (
    <span
      role="img"
      aria-label={nom}
      className={className}
      style={{ position: "relative", display: "inline-block", isolation: "isolate", ...style }}
      {...reste}
    >
      <span aria-hidden="true" style={metal} />
      <img src={marque.icone} alt="" aria-hidden="true" style={relief} draggable={false} />
    </span>
  );
}
